import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, mergeMap } from "rxjs/operators";
import { Claims, Market, Token } from "../../types";
import { AppFeatureEnum } from '../../types/enums/appfeatureenum';
import { AuthService } from "../data/auth.service";
import { OrganizationService } from "../data/organization.service";

@Injectable({
    "providedIn": "root"
})
export class MarketResolver implements Resolve<any>{
    private router = inject(Router);
    private orgService = inject(OrganizationService);
    private authService = inject(AuthService);
    private route = inject(ActivatedRoute);

    resolve (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        const orgId = route.paramMap.get("orgId");
        if (!orgId) {
            this.router.navigateByUrl("/");
            return of(null);
        }

        return this.orgService.getMarketById(+orgId).pipe(
            mergeMap((mkt: Market) => {
                if (!mkt) {
                    this.router.navigateByUrl("/");
                    return of(null);
                }

                if (mkt.constructionStatusId !== 1) {
                    this.router.navigateByUrl("/");
                    return of(null);
                }

                //Check the Markets App Features to make sure the feature being accessed is enabled for the current market
                if ((location.href.indexOf('schedules') > -1 && !mkt.appFeatures.find(af => af.appFeatureId === AppFeatureEnum.Schedules)) ||
                    (location.href.indexOf('recordables') > -1 && !mkt.appFeatures.find(af => af.appFeatureId === AppFeatureEnum.Recordables)) ||
                    (location.href.indexOf('purchaseorders') > -1 && !mkt.appFeatures.find(af => af.appFeatureId === AppFeatureEnum.PurchaseOrders))) {
                    this.router.navigate(['/inactive'], { queryParams: { "marketName": mkt.orgName } });
                    return of(null);
                }

                //Check to make sure the user has a verified auth token (Azure AD authenticated)
                if (!this.authService.isUserLoggedIn()) {
                    this.router.navigateByUrl('/unauthorized');
                    return of(null);
                }

                //Check to make sure the user has the appropriate 'userOrg' claim
                const userOrgs = this.authService.getClaimValue<string>(Claims.userOrgs);

                if (userOrgs.length === 0 || userOrgs.indexOf(mkt.orgId.toString()) > -1) {
                    return of(mkt);
                } else {
                    this.router.navigateByUrl("/forbidden");
                    return of(null);
                }
            }),
            catchError((err) => {
                console.log(err);
                this.router.navigateByUrl("/");
                return of(null);
            }));
    }
}
